'use client';

import { useRef } from 'react';

import type { Chart } from 'chart.js';

import { useThemeKey } from '../chart-theme';
import { useChart } from '../useChart';
import type { ScoreDoughnutProps } from './ScoreDoughnut.types';

function ScoreDoughnut({
  style,
  className,
  score,
  answerRate,
  cutout = '85%',
  label,
  testId = 'ui-score-doughnut',
}: ScoreDoughnutProps) {
  const themeKey = useThemeKey();
  // The plugin below is read once, when the chart is built, so it reads the score it has
  // to draw from here rather than capturing the one it was created with.
  const scoreRef = useRef(score);

  const canvasRef = useChart<'doughnut'>(() => {
    scoreRef.current = score;

    const documentStyle = getComputedStyle(document.body);

    const scoreInTheMiddle = {
      id: 'scoreInTheMiddle',
      beforeDraw: (chart: Chart) => {
        const { ctx, chartArea } = chart;

        if (!ctx || !chartArea) {
          return;
        }

        const { top, bottom, left, right } = chartArea;
        const text = Number(scoreRef.current.toFixed(2)).toString();

        ctx.save();
        ctx.font = `${((bottom - top) / 50).toFixed(2)}em ${documentStyle.getPropertyValue('--font-family-large')}`;
        ctx.fillStyle = documentStyle.getPropertyValue('--color-card-primary');
        ctx.textAlign = 'center';
        ctx.textBaseline = 'alphabetic';

        // Centre the drawn glyphs rather than the em box, so the score sits
        // optically in the middle of the doughnut at any size or font.
        const metrics = ctx.measureText(text);
        const offset =
          (metrics.actualBoundingBoxAscent - metrics.actualBoundingBoxDescent) /
          2;

        ctx.fillText(text, (left + right) / 2, (top + bottom) / 2 + offset);
        ctx.restore();
      },
    };

    return {
      type: 'doughnut',
      data: {
        datasets: [
          {
            data: [
              answerRate.correct,
              answerRate.incorrect,
              answerRate.skipped,
            ],
            borderColor: documentStyle.getPropertyValue('--color-card-soft'),
            borderWidth: 3,
            backgroundColor: [
              documentStyle.getPropertyValue('--color-success-main'),
              documentStyle.getPropertyValue('--color-danger-main'),
              documentStyle.getPropertyValue('--color-card-soft'),
            ],
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        cutout,
        plugins: {
          title: { display: false },
          legend: { display: false },
          tooltip: { enabled: false },
        },
        elements: {
          arc: {
            borderJoinStyle: 'round',
          },
        },
      },
      plugins: [scoreInTheMiddle],
    };
  }, [
    answerRate.correct,
    answerRate.incorrect,
    answerRate.skipped,
    cutout,
    score,
    themeKey,
  ]);

  return (
    <canvas
      ref={canvasRef}
      style={style}
      className={className}
      role="img"
      aria-label={label}
      data-testid={testId}
    />
  );
}

export { ScoreDoughnut };
